import { createBrowserRouter, type RouteObject } from 'react-router';

import { AppProviders } from '@/app/providers';
import { RequireAuth, RequireGuest, RequirePermission } from '@/features/auth/guards';
import { PERMISSIONS } from '@/features/auth/permissions';
import { AdminLayout } from '@/layouts/admin-layout';

import { ForbiddenPage, NotFoundPage, RouteErrorPage } from './error-pages';

/**
 * Route tree.
 *
 * Providers sit inside the router rather than around it so the auth layer can
 * use router hooks: a session that expires mid-request has to redirect, and a
 * provider mounted above the router cannot.
 *
 * Page components are loaded through `lazy` so each route is its own chunk. The
 * shell — providers, guards, layout — stays eager.
 *
 * Feature routes go under the `RequireAuth` branch, wrapped in
 * `RequirePermission` where the section is permission-gated.
 */
export const routes: RouteObject[] = [
  {
    element: <AppProviders />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <RequireGuest />,
        children: [
          {
            path: '/login',
            lazy: async () => {
              const { LoginPage } = await import('@/features/auth/login-page');
              return { Component: LoginPage };
            },
          },
          {
            path: '/forgot-password',
            lazy: async () => {
              const { ForgotPasswordPage } = await import('@/features/auth/forgot-password-page');
              return { Component: ForgotPasswordPage };
            },
          },
          {
            path: '/reset-password',
            lazy: async () => {
              const { ResetPasswordPage } = await import('@/features/auth/reset-password-page');
              return { Component: ResetPasswordPage };
            },
          },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                index: true,
                lazy: async () => {
                  const { DashboardPage } = await import('@/features/dashboard/dashboard-page');
                  return { Component: DashboardPage };
                },
              },
              { path: 'forbidden', element: <ForbiddenPage /> },
              {
                element: <RequirePermission permissions={[PERMISSIONS.contents.view]} />,
                children: [
                  {
                    path: 'content',
                    lazy: async () => {
                      const { ContentListPage } =
                        await import('@/features/content/content-list-page');
                      return { Component: ContentListPage };
                    },
                  },
                  {
                    path: 'content/:id',
                    lazy: async () => {
                      const { ContentEditorPage } =
                        await import('@/features/content/content-editor-page');
                      return { Component: ContentEditorPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.contents.translate]} />,
                children: [
                  {
                    path: 'translations',
                    lazy: async () => {
                      const { TranslationQueuePage } =
                        await import('@/features/content/translation-queue-page');
                      return { Component: TranslationQueuePage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.gates.view]} />,
                children: [
                  {
                    path: 'gates',
                    lazy: async () => {
                      const { GateBoardPage } = await import('@/features/gates/gate-board-page');
                      return { Component: GateBoardPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.media.view]} />,
                children: [
                  {
                    path: 'media',
                    lazy: async () => {
                      const { MediaLibraryPage } =
                        await import('@/features/media/media-library-page');
                      return { Component: MediaLibraryPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.taxonomy.view]} />,
                children: [
                  {
                    path: 'taxonomy',
                    lazy: async () => {
                      const { TaxonomyPage } = await import('@/features/taxonomy/taxonomy-page');
                      return { Component: TaxonomyPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.authors.view]} />,
                children: [
                  {
                    path: 'authors',
                    lazy: async () => {
                      const { AuthorsPage } = await import('@/features/authors/authors-page');
                      return { Component: AuthorsPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.locales.view]} />,
                children: [
                  {
                    path: 'languages',
                    lazy: async () => {
                      const { LocalesPage } = await import('@/features/locales/locales-page');
                      return { Component: LocalesPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.seo.view]} />,
                children: [
                  {
                    path: 'seo',
                    lazy: async () => {
                      const { SeoPage } = await import('@/features/seo/seo-page');
                      return { Component: SeoPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.redirects.view]} />,
                children: [
                  {
                    path: 'redirects',
                    lazy: async () => {
                      const { RedirectsPage } = await import('@/features/redirects/redirects-page');
                      return { Component: RedirectsPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.navigation.manage]} />,
                children: [
                  {
                    path: 'navigation',
                    lazy: async () => {
                      const { MenusPage } = await import('@/features/navigation/menus-page');
                      return { Component: MenusPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.users.view]} />,
                children: [
                  {
                    path: 'users',
                    lazy: async () => {
                      const { UsersPage } = await import('@/features/users/users-page');
                      return { Component: UsersPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.roles.manage]} />,
                children: [
                  {
                    path: 'roles',
                    lazy: async () => {
                      const { RolesPage } = await import('@/features/roles/roles-page');
                      return { Component: RolesPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.settings.manage]} />,
                children: [
                  {
                    path: 'settings',
                    lazy: async () => {
                      const { SettingsPage } = await import('@/features/settings/settings-page');
                      return { Component: SettingsPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.audit.view]} />,
                children: [
                  {
                    path: 'audit',
                    lazy: async () => {
                      const { AuditLogPage } = await import('@/features/audit/audit-log-page');
                      return { Component: AuditLogPage };
                    },
                  },
                ],
              },
              {
                element: <RequirePermission permissions={[PERMISSIONS.import.view]} />,
                children: [
                  {
                    path: 'import',
                    lazy: async () => {
                      const { ImportRunsPage } = await import('@/features/import/import-runs-page');
                      return { Component: ImportRunsPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
